import { makePrioStyles } from '../../theme/utils';
import {
  BasisFilterPicker,
  DateRangePicker,
  MonthRangePicker,
  YearRangePicker,
  BooleanFilterPicker,
  LogikFilterPicker,
  getSingleLabelFromUTSN,
  LIB_TRANSLATION_NAMESPACE,
} from '@prio365/prio365-react-library';
import { isValid, compareAsc, endOfDay, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import FilterPicker, {
  FilterPickerType,
  FilterPickerTypeStrings,
} from './FilterPicker';
import {
  FilterConfig,
  PickerStrings,
  SearchOperation,
  SearchType,
} from './types';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { formatDateRangePickerValue } from './utils';
import { DatePickerLocales } from '@prio365/prio365-react-library/lib/Date/types';

const useStyles = makePrioStyles((theme) => ({
  fitWidth: {
    width: 'fit-content',
  },
}));

const ALLOWED_METHODS_HIERACHY: SearchOperation[] = ['like', 'eq', 'in'];

export interface DynamicFilterProps {
  searchType: SearchType;
  filter: FilterConfig;
  pickerStrings: PickerStrings[];
  setPickerStrings: (parameterName: string, value: PickerStrings[]) => void;
  deletePickerStrings: (parameterName: string) => void;
  handleFetchSearch: () => void;
}

export const DynamicFilterContainer: React.FC<DynamicFilterProps> = (props) => {
  const { filter, deletePickerStrings, handleFetchSearch } = props;

  return (
    <div
      onContextMenu={(e) => {
        e.preventDefault();
        deletePickerStrings(filter?.parameterName);
      }}
    >
      <DynamicFilter
        {...props}
        deletePickerStrings={deletePickerStrings}
        handleFetchSearch={handleFetchSearch}
      />
    </div>
  );
};

export default DynamicFilterContainer;

//#region ------------------------------ DynamicFilter

const DynamicFilter: React.FC<DynamicFilterProps> = (props) => {
  const {
    searchType,
    filter,
    pickerStrings,
    setPickerStrings,
    deletePickerStrings,
    handleFetchSearch,
  } = props;
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const locale: DatePickerLocales =
    (i18n.language?.substring(0, 2) as DatePickerLocales) || 'en';
  const [logicPickerKey, setLogicPickerKey] = useState<number>(0);

  const getFilterMethod = (allowedMethods: string[]): string => {
    for (let i = 0; i < ALLOWED_METHODS_HIERACHY.length; i++) {
      if (allowedMethods.includes(ALLOWED_METHODS_HIERACHY[i])) {
        return ALLOWED_METHODS_HIERACHY[i];
      }
    }
    return allowedMethods[0];
  };

  const handleResetPickerStrings = useCallback(() => {
    setPickerStrings(filter?.parameterName, []);
    setLogicPickerKey((prev) => prev + 1);
    deletePickerStrings(filter?.parameterName);
  }, [deletePickerStrings, filter?.parameterName, setPickerStrings]);

  useEffect(() => {
    if (pickerStrings?.length === 0) {
      handleResetPickerStrings();
    }
  }, [pickerStrings, handleResetPickerStrings]);

  if (FilterPickerTypeStrings?.includes(filter?.pickerType as FilterPickerType))
    return (
      <FilterPicker
        zIndex="50000"
        className={classes.fitWidth}
        type={filter?.pickerType as FilterPickerType}
        parameterName={filter?.parameterName}
        prefixOption={
          filter?.parameterNameTranslated || filter?.parameterName
            ? `${
                filter?.parameterNameTranslated !== ''
                  ? filter?.parameterNameTranslated
                  : filter?.parameterName
              }:`
            : undefined
        }
        value={pickerStrings?.[0]?.value?.split(',') || null}
        onChange={(value) => {
          setPickerStrings(filter?.parameterName, [
            {
              method: getFilterMethod(filter.allowedMethods),
              value: value?.length > 0 ? value.join(',') : null,
            },
          ]);
        }}
        roles={filter?.pickerRoles}
        handleFetchSearch={handleFetchSearch}
        searchType={searchType}
      />
    );

  if (filter?.selectOptions?.[0]) {
    const mappedSelectOptions = filter?.selectOptions?.map((option) => ({
      value: option?.selectValue,
      label: option?.selectValueTranslated,
      searchValue: `${option?.selectValueTranslated} ${option?.selectValue}`,
    }));
    return (
      <BasisFilterPicker
        zIndex="50000"
        onChange={(value) =>
          setPickerStrings(filter?.parameterName, [
            {
              method: getFilterMethod(filter.allowedMethods),
              value: value?.length > 0 ? value.join(',') : null,
            },
          ])
        }
        value={pickerStrings?.[0]?.value?.split(',') || null}
        prefixOption={
          filter?.parameterNameTranslated || filter?.parameterName
            ? `${
                filter?.parameterNameTranslated !== ''
                  ? filter?.parameterNameTranslated
                  : filter?.parameterName
              }:`
            : undefined
        }
        options={mappedSelectOptions}
        placeholder={t('components:filter.all')}
        mode="multiple"
        onControlEnter={handleFetchSearch}
      />
    );
  }

  switch (filter?.parameterType) {
    case 'date':
    case 'dateTime':
      // es-lint-disable-next-line

      const pickerStringsWithoutRel = pickerStrings?.filter(
        (S) => S.method !== 'rel'
      );

      const relPickerString = pickerStrings?.find((S) => S.method === 'rel');

      let [startISOString, endISOString] =
        pickerStringsWithoutRel
          ?.map((S) => S.value)
          .sort((a, b) => compareAsc(new Date(a), new Date(b))) || [];

      return (
        <DateRangePicker
          locale={locale as DatePickerLocales}
          prefixOption={
            filter?.parameterNameTranslated || filter?.parameterName
          }
          placeholder={t(`${LIB_TRANSLATION_NAMESPACE}:select.date.range.all`)}
          key={filter?.parameterName}
          onChange={({ dateValue, utsn }) => {
            if (utsn) {
              setPickerStrings(filter?.parameterName, [
                {
                  method: 'rel',
                  value: utsn?.utsn,
                },
              ]);
            } else {
              setPickerStrings(filter?.parameterName, [
                {
                  method: 'ge',
                  value: dateValue?.[0]
                    ? format(new Date(dateValue[0]), "yyyy-MM-dd'T'HH:mm:ss")
                    : null,
                },
                {
                  method: 'le',
                  value: dateValue?.[1]
                    ? format(
                        endOfDay(new Date(dateValue[1])),
                        "yyyy-MM-dd'T'HH:mm:ss"
                      )
                    : null,
                },
              ]);
            }
          }}
          value={formatDateRangePickerValue(
            [
              startISOString?.length > 5 ? new Date(startISOString) : null,
              endISOString?.length > 5 ? new Date(endISOString) : null,
            ],
            relPickerString?.value
              ? (getSingleLabelFromUTSN(
                  relPickerString?.value,
                  'range',
                  locale as any
                ) as any)
              : null
          )}
        />
      );
    case 'month':
      // es-lint-disable-next-line
      [startISOString, endISOString] =
        pickerStrings
          ?.map((S) => S.value)
          .sort((a, b) => compareAsc(new Date(a), new Date(b))) || [];
      return (
        <MonthRangePicker
          locale={locale}
          prefixOption={
            filter?.parameterNameTranslated || filter?.parameterName
          }
          onChange={(value) =>
            setPickerStrings(filter?.parameterName, [
              {
                method: 'ge',
                value:
                  value?.[0] && isValid(value[0])
                    ? format(value[0], "yyyy-MM-dd'T'HH:mm:ss")
                    : null,
              },
              {
                method: 'le',
                value:
                  value?.[1] && isValid(value[1])
                    ? format(value[1], "yyyy-MM-dd'T'HH:mm:ss")
                    : null,
              },
            ])
          }
          value={[
            startISOString?.length > 5 && isValid(new Date(startISOString))
              ? new Date(startISOString)
              : null,
            endISOString?.length > 5 && isValid(new Date(endISOString))
              ? new Date(endISOString)
              : null,
          ]}
          className={classes.fitWidth}
        />
      );
    case 'year':
      // es-lint-disable-next-line
      [startISOString, endISOString] =
        pickerStrings
          ?.map((S) => S.value)
          .sort((a, b) => compareAsc(new Date(a), new Date(b))) || [];

      return (
        <YearRangePicker
          locale={locale}
          prefixOption={
            filter?.parameterNameTranslated || filter?.parameterName
          }
          onChange={(value) =>
            setPickerStrings(filter?.parameterName, [
              {
                method: 'ge',
                value:
                  value?.[0] && isValid(value[0])
                    ? format(value[0], "yyyy-MM-dd'T'HH:mm:ss")
                    : null,
              },
              {
                method: 'le',
                value:
                  value?.[1] && isValid(value[1])
                    ? format(value[1], "yyyy-MM-dd'T'HH:mm:ss")
                    : null,
              },
            ])
          }
          value={[
            startISOString?.length > 5 && isValid(new Date(startISOString))
              ? new Date(startISOString)
              : null,
            endISOString?.length > 5 && isValid(new Date(endISOString))
              ? new Date(endISOString)
              : null,
          ]}
          className={classes.fitWidth}
        />
      );
    case 'boolean':
      return (
        <BooleanFilterPicker
          prefixOption={
            filter?.parameterNameTranslated || filter?.parameterName
          }
          className={classes.fitWidth}
          value={
            pickerStrings?.[0]?.value
              ? JSON.parse(pickerStrings?.[0]?.value || 'null')
              : null
          }
          onChange={(value) => {
            setPickerStrings(filter?.parameterName, [
              {
                method: getFilterMethod(filter.allowedMethods),
                value: JSON.stringify(value),
              },
            ]);
          }}
        />
      );
    default:
      return (
        <div
          onContextMenu={(e) => {
            e.preventDefault();
            handleResetPickerStrings();
          }}
        >
          <LogikFilterPicker
            key={logicPickerKey}
            prefixOption={
              filter?.parameterNameTranslated || filter?.parameterName
            }
            parameterType={filter?.parameterType as any}
            className={classes.fitWidth}
            allowedMethods={filter?.allowedMethods || []}
            value={pickerStrings?.length > 0 ? (pickerStrings as any) : []}
            onChange={(value: any) => {
              const isEmpty =
                !value ||
                (Array.isArray(value) &&
                  (value.length === 0 ||
                    value.every((v: any) => !v.value || v.value === '')));
              if (isEmpty) {
                deletePickerStrings(filter?.parameterName);
              } else {
                setPickerStrings(filter?.parameterName, value);
              }
            }}
            onControlEnter={handleFetchSearch}
          />
        </div>
      );
  }
};
//#endregion
